import { useQuery } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { Device } from "@/shared/domain/devices";

/**
 * Returns the query key for fetching all devices by organization.
 */
export function getQueryKey(orgId: string | undefined) {
  return ["allDevicesByOrg", orgId] as const;
}

/**
 * Fetches all devices for an organization, handling pagination internally.
 * Returns a flat array of all Device objects once fully loaded.
 *
 * Use this hook when you need the complete list of devices upfront
 * (e.g., for autocomplete/select components).
 *
 * For paginated/incremental loading, use useDevicesByOrg instead.
 */
export function useAllDevicesByOrg(orgId: string | undefined) {
  const { devices } = useDomainServices();

  return useQuery<Device[], Error>({
    queryKey: getQueryKey(orgId),
    queryFn: ({ signal }) => {
      if (orgId === undefined) {
        return Promise.reject(new Error("Invalid orgId"));
      }

      return devices.getAllDevicesByOrg(orgId, {
        abortSignal: signal,
      });
    },
    enabled: orgId !== undefined,
    staleTime: 300_000, // 5 minutes
  });
}
